import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api.js";
import { useI18n } from "../lib/i18n.jsx";
import { Plus } from "lucide-react";
import NewCaseDialog from "../components/NewCaseDialog.jsx";
import ConfigurableTable, { ColumnConfigButton, useColumnConfig } from "../components/ConfigurableTable.jsx";
import SavedViewsBar from "../components/SavedViewsBar.jsx";
import PageHeader from "../components/PageHeader.jsx";

export default function ServiceCasesPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [enums, setEnums] = useState(null);
  const [filters, setFilters] = useState({ brand: "", branch: "", status: "", priority: "", open_only: false });
  const [showNew, setShowNew] = useState(false);
  const colConfig = useColumnConfig("service_cases");

  const load = async () => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== "" && v != null) params.append(k, v);
    });
    const { data } = await api.get(`/service-cases?${params}`);
    setCases(data);
  };

  useEffect(() => {
    api.get("/enums").then((r) => setEnums(r.data));
  }, []);
  useEffect(() => {
    load();
  }, [JSON.stringify(filters)]);

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1600px] mx-auto">
      <PageHeader
        overline="Oficinas"
        title={t("service_cases")}
        subtitle={`${cases.length} registo${cases.length === 1 ? "" : "s"} — gestão de ordens de serviço.`}
        actions={
          <>
            <ColumnConfigButton config={colConfig} viewKey="service_cases" />
            <button
              data-testid="new-case-btn"
              onClick={() => setShowNew(true)}
              className="bg-ronil-navy hover:bg-ronil-navy-hover text-white rounded-md px-4 py-2 text-sm font-heading font-semibold flex items-center gap-2 shadow-sm hover:shadow-elevation transition-all"
            >
              <Plus className="w-4 h-4" /> {t("new_case")}
            </button>
          </>
        }
      />

      <div className="bg-white border border-slate-200 rounded-lg p-4 mb-4 grid grid-cols-2 md:grid-cols-5 gap-3 shadow-soft-card">
        <Select label={t("status")} value={filters.status} onChange={(v) => setFilters({ ...filters, status: v })} options={[{ value: "", label: t("all") }, ...(enums?.service_case_statuses || []).map((s) => ({ value: s, label: s }))]} />
        <Select label="Marca" value={filters.brand} onChange={(v) => setFilters({ ...filters, brand: v })} options={[{ value: "", label: t("all") }, ...(enums?.brands || []).map((s) => ({ value: s, label: s }))]} />
        <Select label="Filial" value={filters.branch} onChange={(v) => setFilters({ ...filters, branch: v })} options={[{ value: "", label: t("all") }, ...(enums?.branches || []).map((s) => ({ value: s, label: s }))]} />
        <Select label={t("priority")} value={filters.priority} onChange={(v) => setFilters({ ...filters, priority: v })} options={[{ value: "", label: t("all") }, ...(enums?.service_case_priorities || []).map((s) => ({ value: s, label: s }))]} />
        <label className="flex items-end gap-2 text-xs text-slate-700 pb-1 font-medium">
          <input
            type="checkbox"
            data-testid="filter-open-only"
            checked={filters.open_only}
            onChange={(e) => setFilters({ ...filters, open_only: e.target.checked })}
            className="accent-ronil-navy"
          />
          {t("open_cases_only")}
        </label>
      </div>

      <div className="mb-3 px-1">
        <SavedViewsBar viewKey="service_cases" config={colConfig} />
      </div>

      <ConfigurableTable viewKey="service_cases" config={colConfig} rows={cases} emptyMessage={t("no_records")} />

      {showNew && enums && (
        <NewCaseDialog enums={enums} onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />
      )}
    </div>
  );
}

function Select({ label, value, onChange, options }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest text-[#6C757D]">{label}</span>
      <select
        data-testid={`filter-${label.toLowerCase()}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full mt-0.5 px-2 py-1.5 border border-gray-300 rounded-sm bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#1F3864]/30"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </label>
  );
}
